import {makeAutoObservable, runInAction} from 'mobx';
import {Client} from '../../../Agent/ContactForm/ContactFormStore/types';
import {Approval} from './Approval';
import {Quote} from './Quote';
import {Health} from './Health';
import {Prequal} from './Prequal';
import {QuoteHistory, QuoteHistoryData} from './types';
import {SSNData} from '../../../Agent/Tools/SSN';
import {Physician, Prescription} from '../../../Agent/Tools/RX';
import {fieldException} from '../../../APIs/fixtures/fieldException';
import {ParameterStoreKeys, parameterRefs} from './ParameterRefXref';
import {Banking} from './Banking';
import moment from 'moment';
import {CalcfocusReportRequest, RecommendedProductsResponse} from '@amplify/athena-models';
import {AllProductType} from '../../../CaseManager/Forms';
import {InstantAppModalPage} from '../../../Agent/Quotes/InstantAppModal';
import {Parameter} from './Parameter';

type FormTypes = 'prequal' | 'quote' | 'preapproval' | 'underwriting' | 'banking';

const BASIC_KEYS = new Set([
    'email',
    'emailAddress',
    'address',
    'addressCity',
    'addressState',
    'addressZip',
    'addressZipCode',
    'state',
    'phoneNumber',
]);

const prosperNotChosenReasonOptionsRenamedValuesMap: Record<string, string> = {
    Stateavailability: 'EligibilityStateAvailability',
    Overunderagelimits: 'EligibilityOverUnderAgeLimits',
    Belowminimumpremium: 'EligibilityBelowMinimumPremium',
    Belowminimumfaceamount: 'EligibilityBelowMinimumFaceAmount',
    Clientwantsmultiplepolicieswithsamecompany: 'ClientRequestedClientwantsMultiplePolicieswithsameCarrier',
    OwnerPayerdifferent: 'CapabilityOwnerPayorDifferent',
    Replacementissue: 'CapabilityReplacementIssue',
    OtherReason: 'OtherOtherReason',
};

export class ParameterStore {
    formPreQual = false;
    formQuote = false;
    formPreApproval = false;
    formProsperUWStatus: string = 'UNINITIALIZED';
    formProsperCaseStartDate: string | null = null;
    prequalCurrentStep: number = 0; // Track current step in multi-step prequal form
    prosperActiveModalPage: InstantAppModalPage | null = null;
    prosperRequest: CalcfocusReportRequest | null = null;
    prosperDecision: string | null = null;
    prosperDeclineReason: string | null = null;

    // The Agenium application ID
    applicationId: string | null = null;
    // The application ID in Amplify system
    instantIulApplicationId: string | null = null;
    instantIulMultiQuestion: boolean | null = null;
    instantIulStartedByCustomer: boolean | null = null;
    instantIulWorkedOnByAgent: boolean | null = null;
    dealId: string | null = null;
    eAppSubmitted: Date | null = null;
    eAppApproved: Date | null = null;
    eAppDeclined: Date | null = null;
    eAppReferredToManualUW: Date | null = null;
    eAppDocusignCompleted: Date | null = null;

    formid: string = '';
    submitted: boolean = false;

    formCreatedAt: Date | null = null;

    formInitiallySubmittedAt: Date | null = null;

    formDirty: Map<string, boolean> = new Map();

    contact: string = '';
    name: string = '';
    agent: string = ''; // the original agent who created this form
    updated: string = ''; // the date this form was last updated
    updatedTimestamp: string = ''; // the time this form was last updated
    created: string = ''; // the date this form was created
    createTimestamp: number | null = null; //timestamp of form create

    approval: Approval = new Approval();
    health: Health = new Health();
    prequal: Prequal = new Prequal();
    banking: Banking = new Banking();

    // these are all the parameters from quote-questions being loaded
    // quote is for single quote, quoteCombination is null but gets set when combination is clicked
    quote: Quote = new Quote();
    quoteCombination: Quote = new Quote();

    currentQuoteId: number | null = null;
    singleQuoteData: QuoteHistoryData | null = null;
    combinedQuoteData: QuoteHistoryData | null = null;

    quoteHistory: QuoteHistory[] = [];
    quoteCombinationHistory: Quote = new Quote();

    walkthroughData: any = {};
    // Raw walkthrough data from CRM API - used for Prosper Flow detection and recommendations
    walkthroughRawData: any = null;
    qualifiedProducts: AllProductType[] = [];
    recommendedProductsResponse: RecommendedProductsResponse | null = null;
    loadingClientData: boolean = false;

    rxRecords: {
        drugs: Prescription[];
        physician: Physician[];
        physicianData: Physician[];
    } | null = null;
    ssnData: SSNData[] | null = null;

    // AB Flags calculated for this form
    abFlags: Record<string, boolean | string> = {};

    hhiWarningInfo: {
        thresholdPercentage: number | null; // 0.04 or 0.05
        thresholdAmount: number | null; // Calculated dollar amount
        wasDisplayed: boolean; // Whether warning was shown
        calculatedAt: Date | null; // When it was calculated
    } = {
        thresholdPercentage: null,
        thresholdAmount: null,
        wasDisplayed: false,
        calculatedAt: null,
    };

    public static version = '11';

    fetchRecommendedProducts = async () => {
        const {updateRecommendationProducts} = await import('../../../APIs/recommendationUtils');
        if (this.contact) {
            await updateRecommendationProducts(this.contact, this);
        }
    };

    loadParamsFromSavedForm = async (r: any, options?: {onlyBasic?: boolean}) => {
        const lookup = new Map<
            string,
            {
                p: string;
                v: any;
            }
        >();

        [r.parameters, r.health, r.quote, r.quoteCombination, r.approval, r.banking].forEach((a) => {
            if (a != null) {
                a.forEach((x: any) => {
                    if (!options?.onlyBasic || BASIC_KEYS.has(x.p)) {
                        // A param name can appear in more than one store (e.g. `kidsNumber` lives in
                        // both prequal and approval). Stores are merged in order, so a later store with
                        // an empty value would clobber an earlier populated one. Don't let that happen.
                        const existing = lookup.get(x.p);
                        if (
                            existing != null &&
                            existing.v !== '' &&
                            existing.v != null &&
                            (x.v === '' || x.v == null)
                        ) {
                            return;
                        }
                        lookup.set(x.p, {p: x.p, v: x.v});
                    }
                });
            }
        });

        [this.prequal, this.health, this.quote, this.quoteCombination, this.approval, this.banking].forEach((a) => {
            if (a != null) {
                Object.entries(a).forEach(([x, p]: [string, Parameter]) => {
                    // ref refers to the field name in formData coming from dynamoDB
                    // x referes to the store object's in MEMORY refernce.
                    // check if this field exists in the exception object.
                    let fieldInDB = x;
                    if (fieldException[fieldInDB] && !lookup.get(fieldInDB)) {
                        while (fieldException[fieldInDB]) fieldInDB = fieldException[fieldInDB];
                    }
                    // special handling for employer's address ENG-269
                    const ref = lookup.get(fieldInDB);
                    if (ref?.p === 'employerStrtAddr')
                        ref.v = `${ref.v} ${lookup.get('employerCity')?.v} ${
                            lookup.get('employerState')?.v
                        } ${lookup.get('employerZip')?.v}`;
                    if (ref != null) {
                        p.value = ref.v;
                    }
                    // Special handling for hasSelectedProduct - ensure it stays false if not in the database
                    if (x === 'hasSelectedProduct' && ref == null && p.value !== true) {
                        p.value = false;
                    }
                    // handle prosperNotChosenReason updated values
                    if (ref?.p === 'prosperNotChosenReason') {
                        p.value = prosperNotChosenReasonOptionsRenamedValuesMap[ref.v] || ref.v;
                    }
                });
            }
        });

        // Debug: log address fields restored from saved form
        console.log(
            '[ADDR_DEBUG] loadParamsFromSavedForm - address:',
            this.approval.address?.value,
            'city:',
            this.approval.addressCity?.value,
            'state:',
            this.approval.state?.value,
            'zip:',
            this.approval.addressZipCode?.value,
        );
        console.log(
            '[ADDR_DEBUG] loadParamsFromSavedForm - clientAddressVerified:',
            this.approval.clientAddressVerified?.value,
            'snapshot:',
            this.approval.clientAddressVerifiedSnapshot?.value,
        );

        runInAction(() => {
            this.contact = r.contact;
            this.name = r.name;
            this.formCreatedAt = r.formCreatedAt;
            this.formInitiallySubmittedAt = r.formInitiallySubmittedAt;
            this.created = r.created;
            this.createTimestamp = r.createTimestamp;

            if (!options?.onlyBasic) {
                this.formid = r.formid;
                this.quoteHistory = r.quoteHistory;
                this.quoteCombinationHistory = r.quoteCombinationHistory;

                this.submitted = r.submitted;

                this.formPreQual = r.formPreQual;
                this.formQuote = r.formQuote;
                this.formPreApproval = r.formPreApproval;
                this.formProsperUWStatus = r.formProsperUWStatus;
                this.formProsperCaseStartDate = r.formProsperCaseStartDate;
                this.prequalCurrentStep = r.prequalCurrentStep || 0;
                this.prosperActiveModalPage = r.prosperActiveModalPage;
                this.prosperRequest = r.prosperRequest;
                this.prosperDecision = r.prosperDecision;
                this.prosperDeclineReason = r.prosperDeclineReason;

                this.applicationId = r.applicationId;
                this.instantIulApplicationId = r.instantIulApplicationId;
                this.instantIulStartedByCustomer = r.instantIulStartedByCustomer;
                this.instantIulWorkedOnByAgent = r.instantIulWorkedOnByAgent;

                this.dealId = r.dealId;
                this.instantIulMultiQuestion = r.instantIulMultiQuestion;

                this.rxRecords = r.rxRecords;
                this.ssnData = r.ssnData;

                this.eAppSubmitted = r.eAppSubmitted;
                this.eAppApproved = r.eAppApproved;
                this.eAppDeclined = r.eAppDeclined;
                this.eAppReferredToManualUW = r.eAppReferredToManualUW;
                this.eAppDocusignCompleted = r.eAppDocusignCompleted;

                // Load AB flags if present
                this.abFlags = r.abFlags ?? {};
                if (r.abFlags && Object.keys(r.abFlags).length > 0) {
                    console.log(`Loaded AB flags from saved form ${r.formid}:`, r.abFlags);
                } else {
                    console.log(`No AB flags found in saved form ${r.formid}`);
                }

                // Hydrate address verification from saved state
                this.hydrateAddressVerification();
            }
        });
    };

    private hydrateAddressVerification = () => {
        // Lazy import to avoid circular dependency
        const RootStore = require('../RootStore').default;
        const store = RootStore.addressVerificationStore;

        const hydrateKey = (
            verifiedParam: Parameter,
            snapshotParam: Parameter,
            addressKey: 'client' | 'policyOwner',
            currentFields: {street: string; city: string; state: string; zip: string; unit?: string},
        ) => {
            if (!verifiedParam.value) {
                return;
            }
            try {
                const snapshot = JSON.parse(snapshotParam.value);
                const matches =
                    snapshot.street === currentFields.street &&
                    snapshot.city === currentFields.city &&
                    snapshot.state === currentFields.state &&
                    snapshot.zip === currentFields.zip;
                if (matches) {
                    store.hydrate(addressKey, snapshot);
                } else {
                    verifiedParam.value = '';
                    snapshotParam.value = '';
                }
            } catch (e) {
                verifiedParam.value = '';
                snapshotParam.value = '';
            }
        };

        hydrateKey(this.approval.clientAddressVerified, this.approval.clientAddressVerifiedSnapshot, 'client', {
            street: this.approval.address?.value || '',
            city: this.approval.addressCity?.value || '',
            state: this.approval.state?.value || '',
            zip: this.approval.addressZipCode?.value || '',
        });

        hydrateKey(
            this.approval.policyOwnerAddressVerified,
            this.approval.policyOwnerAddressVerifiedSnapshot,
            'policyOwner',
            {
                street: this.approval.policyOwnerStreetAddress?.value || '',
                city: this.approval.policyOwnerCity?.value || '',
                state: this.approval.policyOwnerState?.value || '',
                zip: this.approval.policyOwnerZipcode?.value || '',
            },
        );
    };

    markFormDirty = (formType: FormTypes | null | undefined) => {
        if (formType && !this.formDirty.has(formType)) {
            runInAction(() => {
                this.formDirty.set(formType, true);
            });
        }
    };

    unmarkFormDirty = (formType: FormTypes | null | undefined) => {
        if (formType) {
            runInAction(() => {
                this.formDirty.delete(formType);
            });
        }
    };

    resetFormDirty = () => {
        runInAction(() => {
            this.formDirty.clear();
        });
    };

    updateApproval = (client: Partial<Client>) => {
        this.approval.firstName.setValue(client.firstName);
        this.approval.lastName.setValue(client.lastName);
        this.approval.emailAddress.setValue(client.emailAddress);
        this.approval.phoneNumber.setValue(client.phoneNumber);
        this.approval.address.setValue(client.address);
        this.approval.addressZipCode.setValue(client.addressZipCode);
        this.approval.birthCountry.setValue(client.birthCountry);
        this.approval.addressCity.setValue(client.addressCity);
        this.approval.agentName.setValue(client.agentName);
        // Sync notes fields in both approval and prequal
        // when coming from live call tab
        this.approval.notes.setValue(client.notes);
        this.prequal.notes.setValue(client.notes);
    };

    onValueChanged = (
        formType: 'prequal' | 'quote-single' | 'quote-combination' | 'preapproval' | 'banking' | 'prosper' | null,
        p?: Parameter,
        value?: any,
        name?: string,
    ): void => {
        const formSource = ['quote-single', 'quote-combination'].includes(formType || '')
            ? 'quote'
            : (formType as FormTypes);

        this.markFormDirty(formSource);

        if (p == null) {
            return;
        }

        runInAction(() => {
            p.setValue(value);

            switch (formType) {
                case 'prequal':
                    // first handle refs
                    if (name && parameterRefs.prequal.hasOwnProperty(name)) {
                        const otherParamType = parameterRefs.prequal[name as keyof typeof parameterRefs.prequal];
                        if (otherParamType && otherParamType == ParameterStoreKeys.approval) {
                            (this.approval[name as keyof typeof this.approval] as Parameter).setValue(value);
                        }
                    }
                    if (!this.formPreQual) this.formPreQual = true;
                    break;
                case 'quote-single':
                case 'quote-combination':
                    if (!this.formQuote) this.formQuote = true;
                    break;
                case 'preapproval':
                    // first handle refs
                    if (name && parameterRefs.approval.hasOwnProperty(name)) {
                        const otherParamType = parameterRefs.approval[name as keyof typeof parameterRefs.approval];
                        if (otherParamType && otherParamType == ParameterStoreKeys.prequal) {
                            (this.prequal[name as keyof typeof this.prequal] as Parameter).setValue(value);
                        }
                    }
                    if (!this.formPreApproval) this.formPreApproval = true;
                    break;
                case 'banking':
                    break;
                default:
                    break;
            }

            localStorage.setItem(
                'active-form-data',
                JSON.stringify(
                    JSON.stringify({
                        approval: this.approval,
                        health: this.health,
                        quote: this.quote,
                        quoteCombination: this.quoteCombination,
                    }),
                ),
            ); // store in case we need to recover
        });
    };

    onValueChangedPreQual = (p?: Parameter & {name?: string}, value?: any, name?: string): void => {
        // Special handling for usCitizenship field
        if (p?.name === 'usCitizenship' || p?.id === 'usCitizenship') {
            // Sync the same value to approval (both use display values now)
            this.approval.usCitizenship.setValue(value);

            // Also handle backward compatibility with isUSCitizen
            const booleanValue = value === 'Yes' || value === 'Permanent Resident' ? true : value ? false : '';
            this.prequal.isUSCitizen.setValue(booleanValue);
        }

        // Sync common fields between prequal and approval
        if (p?.id === 'firstName') {
            this.approval.firstName.setValue(value);
        }
        if (p?.id === 'lastName') {
            this.approval.lastName.setValue(value);
        }
        if (p?.id === 'middleName') {
            this.approval.middleName.setValue(value);
        }
        if (p?.id === 'suffix') {
            this.approval.suffix.setValue(value);
        }
        // Sync date of birth fields
        if (p?.id === 'bmonth') {
            this.approval.bmonth.setValue(value);
        }
        if (p?.id === 'bday') {
            this.approval.bday.setValue(value);
        }
        if (p?.id === 'byear') {
            this.approval.byear.setValue(value);
        }
        // Sync height and weight
        if (p?.id === 'height') {
            this.approval.height.setValue(value);
        }
        if (p?.id === 'weight') {
            this.approval.weight.setValue(value);
        }

        // Always call the default sync
        this.onValueChanged('prequal', p, value, name || p?.name);

        // Special case: Sync HIV/AIDS fields between health and approval
        if (p?.id === 'HIVAIDS') {
            this.approval.HIV.setValue(value);
            this.prequal.HIV.setValue(value);
        }

        // Special case: Sync old isUSCitizen field with new usCitizenship field
        if (p?.id === 'isUSCitizen') {
            // Convert boolean to display value for both prequal and approval
            const displayValue = value === true ? 'Yes' : value === false ? 'No U.S. Visa - TIN only' : '';
            this.prequal.usCitizenship.setValue(displayValue);
            this.approval.usCitizenship.setValue(displayValue);
        }
    };

    onValueChangedPreApproval = (p?: Parameter & {name?: string}, value?: any): void => {
        // Special handling for usCitizenship field
        if (p?.name === 'usCitizenship' || p?.id === 'usCitizenship') {
            this.prequal.usCitizenship.setValue(value);

            // Also handle backward compatibility with isUSCitizen
            const booleanValue = value === 'Yes' || value === 'Permanent Resident' ? true : value ? false : '';
            this.prequal.isUSCitizen.setValue(booleanValue);
        }

        // Sync common fields between approval and prequal
        if (p?.id === 'firstName') {
            this.prequal.firstName.setValue(value);
        }
        if (p?.id === 'lastName') {
            this.prequal.lastName.setValue(value);
        }
        if (p?.id === 'middleName') {
            this.prequal.middleName.setValue(value);
        }
        if (p?.id === 'suffix') {
            this.prequal.suffix.setValue(value);
        }
        // Sync date of birth fields
        if (p?.id === 'bmonth') {
            this.prequal.bmonth.setValue(value);
        }
        if (p?.id === 'bday') {
            this.prequal.bday.setValue(value);
        }
        if (p?.id === 'byear') {
            this.prequal.byear.setValue(value);
        }
        // Sync height and weight
        if (p?.id === 'height') {
            this.prequal.height.setValue(value);
        }
        if (p?.id === 'weight') {
            this.prequal.weight.setValue(value);
        }

        // Always call the default sync
        this.onValueChanged('preapproval', p, value, p?.name);

        // Special case: Sync HIV/AIDS fields between approval and health
        if (p?.id === 'HIV') {
            this.health.HIVAIDS.setValue(value);
            this.prequal.HIV.setValue(value);
        }
    };

    onValueChangedQuote = (p?: Parameter, value?: any, formType?: 'single-quote' | 'combination-quote'): void => {
        if (formType === 'combination-quote') {
            this.onValueChanged('quote-combination', p, value);
        } else {
            this.onValueChanged('quote-single', p, value);
        }
    };

    onValueChangedBanking = (p?: Parameter, value?: any): void => {
        this.onValueChanged('banking', p, value);
    };

    getQuote = (formType?: 'single-quote' | 'combination-quote'): Quote | null => {
        if (!formType || formType === 'single-quote') return this.quote;
        if (formType === 'combination-quote' && this.quoteCombination.isCombination) return this.quoteCombination;
        else return null;
    };

    getLatestQuoteData = (): QuoteHistoryData | null => {
        const latestQuoteEntry = this.quoteHistory.at(-1);
        return this.singleQuoteData ?? latestQuoteEntry?.singleQuote ?? null;
    };

    updateProsperStatus(status: 'OPEN_AP' | 'CLOSED') {
        if (!this.formProsperCaseStartDate) {
            this.formProsperCaseStartDate = moment().toString();
            this.formProsperUWStatus = status;
        }
    }

    constructor() {
        makeAutoObservable(this);
    }
}

export {Parameter};
